import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


function VerifiedNotice() {
  return (
    <p className="flex items-center gap-1 text-green-700 font-medium">
      <CheckCircle className="h-3.5 w-3.5" /> Address verified by USPS.
    </p>
  );
}


function CorrectedSuggestion({ result, entered, onAccept }) {
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-1 text-amber-700 font-medium">
        <AlertTriangle className="h-3.5 w-3.5" /> USPS suggests a corrected version
      </p>
      <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
        {result.corrections.map((c, i) => (<li key={`${c}-${i}`}>{c}</li>))}
      </ul>
      <p className="font-mono text-[11px] bg-white border rounded p-2">
        {result.standardized.address}
        {result.standardized.address_line_2 ? `, ${result.standardized.address_line_2}` : ''}<br />
        {result.standardized.city}, {result.standardized.state} {result.standardized.postal_code}
      </p>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="default"
          onClick={() => onAccept && onAccept(result.standardized)}
          data-testid="address-use-corrected"
        >
          Use corrected
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onAccept && onAccept(entered)}
          data-testid="address-use-entered"
        >
          Keep entered
        </Button>
      </div>
    </div>
  );
}


function InvalidNotice({ result, entered, onAccept }) {
  return (
    <div className="space-y-1 text-red-600">
      <p className="flex items-center gap-1 font-medium">
        <AlertTriangle className="h-3.5 w-3.5" /> USPS could not verify this address.
      </p>
      {(result.warnings || []).slice(0, 2).map((w, i) => (
        <p key={`${w}-${i}`} className="text-[11px]">{w}</p>
      ))}
      <Button
        size="sm"
        variant="outline"
        onClick={() => onAccept && onAccept(entered)}
        data-testid="address-bypass"
      >
        Use anyway
      </Button>
    </div>
  );
}


function ValidationOutcome({ result, entered, onAccept }) {
  if (result.configured === false) return null; // backend silent-passthrough
  if (result.error) {
    return <p className="text-red-600">{result.error}</p>;
  }
  if (result.valid && result.corrections.length === 0 && result.warnings.length === 0) {
    return <VerifiedNotice />;
  }
  if (result.valid && result.corrections.length > 0 && result.standardized) {
    return <CorrectedSuggestion result={result} entered={entered} onAccept={onAccept} />;
  }
  return <InvalidNotice result={result} entered={entered} onAccept={onAccept} />;
}


/**
 * Reusable USPS address validator. Pass the structured address fields and an
 * `onAccept` callback that receives the chosen (entered or USPS-corrected)
 * address. The component handles its own modal state.
 *
 * This component is non-blocking: if the server returns `configured: false`
 * (USPS keys not set on the backend) we silently let the user proceed.
 */
export default function AddressValidator({
  address,
  address_line_2,
  city,
  state,
  postal_code,
  onAccept,
  triggerLabel = 'Validate Address',
  className = '',
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const entered = { address, address_line_2, city, state, postal_code };

  const submit = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/addresses/validate`, {
        address,
        address_line_2: address_line_2 || '',
        city,
        state,
        postal_code: postal_code || '',
      });
      setResult(data);
      // Auto-accept the entered/corrected address when no admin action needed.
      if (data.configured === false) {
        if (onAccept) onAccept(entered);
      } else if (data.valid && data.corrections.length === 0 && data.warnings.length === 0) {
        if (onAccept) onAccept(data.standardized);
      }
    } catch {
      setResult({ error: 'Address service unavailable.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={className} data-testid="address-validator">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={submit}
        disabled={busy}
        data-testid="address-validate-btn"
      >
        {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Checking…</> : triggerLabel}
      </Button>
      {result && result.configured !== false && (
        <div
          className="mt-3 rounded-md border border-border bg-muted/20 p-3 text-xs space-y-2"
          data-testid="address-validate-result"
        >
          <ValidationOutcome result={result} entered={entered} onAccept={onAccept} />
        </div>
      )}
    </div>
  );
}
